import type { TFunction } from 'i18next';
import type { Notified } from '../../api/workEnvironmentTypes';

const K = 'hms.reminder';

/** Sent within a minute counts as now, as the queue goes out every few minutes */
const NOW_MS = 60 * 1000;

const dayOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const DAY_MS = 24 * 60 * 60 * 1000;

/** «i dag kl. 16:00», «i morgen kl. 08:00» or «mandag 4. januar kl. 08:00» */
const whenText = (sendsAt: Date, now: Date, t: TFunction, language: string) => {
  const locale = language === 'en' ? 'en-GB' : 'nb-NO';
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(sendsAt);
  const days = Math.round((dayOf(sendsAt) - dayOf(now)) / DAY_MS);
  if (days === 0) return t(`${K}.today`, { time });
  if (days === 1) return t(`${K}.tomorrow`, { time });
  const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(sendsAt);
  return t(`${K}.day`, { date, time });
};

/** Sent now, or later within the working hours of the team, in the texts under the given key */
const sentOrLater = (key: string, sendsAt: string, t: TFunction, language: string, now: Date) => {
  const when = new Date(sendsAt);
  return when.getTime() - now.getTime() < NOW_MS
    ? t(`${key}.sent`)
    : t(`${key}.later`, { when: whenText(when, now, t, language) });
};

/**
 * What the user is told after sending a reminder. It waits for the working hours of the team,
 * and without a time nobody gets a notification.
 */
export const deliveryText = (sendsAt: string | null, t: TFunction, language: string, now = new Date()) =>
  sendsAt === null ? t(`${K}.notNotified`) : sentOrLater(K, sendsAt, t, language, now);

const NOTIFIED = 'hms.notified';

/** What the user is told after an action that notifies others, such as asking for a review */
export const notifiedText = (notified: Notified | undefined, t: TFunction, language: string, now = new Date()) => {
  const sendsAt = notified?.sendsAt;
  if (sendsAt === undefined) return null;
  return sendsAt === null ? t(`${NOTIFIED}.nobody`) : sentOrLater(NOTIFIED, sendsAt, t, language, now);
};

const INVITATION = 'hms.invitation';

/**
 * When the team gets the invitation to answer, as «Send now» waits for the working hours of the team. Null when it
 * went out right away, or the API did not say.
 */
export const invitationLaterText = (
  sendsAt: string | null | undefined,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  if (!sendsAt) return null;
  const when = new Date(sendsAt);
  return when.getTime() - now.getTime() < NOW_MS
    ? null
    : t(`${INVITATION}.later`, { when: whenText(when, now, t, language) });
};

export type ReminderError = 'noChannels' | 'noRecipients' | 'cannotRemind' | 'failed';

const KNOWN: ReminderError[] = ['noChannels', 'noRecipients', 'cannotRemind'];

/** Why a reminder did not go out, from the code the API sends */
export const reminderError = (error: unknown): ReminderError | null => {
  if (!error) return null;
  const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
  return KNOWN.find((known) => known === code) ?? 'failed';
};

export const reminderErrorText = (error: unknown, t: TFunction) => {
  const reason = reminderError(error);
  return reason && t(`${K}.errors.${reason}`);
};

export type ReminderStatus = { sent: string | null; failed: string | null };

/** What the page says after the user sent a reminder, from the state of the request */
export const reminderStatus = (
  request: { data?: { sendsAt: string | null } | undefined; error: unknown },
  t: TFunction,
  language: string,
  now = new Date(),
): ReminderStatus => ({
  sent: request.data ? deliveryText(request.data.sendsAt, t, language, now) : null,
  failed: reminderErrorText(request.error, t),
});
